import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Box, Button, Card, CircularProgress, Grow, Icon, Popper, Stack, Tooltip } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";

import { LAYOUT_MARGIN } from "Configs/home";

import { useAppSelector } from "Hooks/useRedux";
import useSurveys from "Hooks/useSurveys";
import usePaths from "Hooks/usePaths";
import useStations from "Hooks/useStations";

import { toggleVisibility as toggleLocationData } from "Redux/locationDataSlice";
import { setMapData } from "Redux/mapSlice";

import Delete from "./Delete";
import Filter from "./Filter";
import Zone from "./Zone";
import LayerControl from "./layerControl";
import Inventory from "./Inventory";
import Time from "./Time";
import Cars from "./cars";
import Drivers from "./drivers";

type OptionType = "Menu" | "Button" | "Toggle";
interface IOption {
  icon: string;
  active?: boolean;
  type: OptionType;
  toolTipText?: string;
  content?: any;
  onClick?: () => void;
}

export default function Toolbox() {
  const dispatch = useDispatch();

  const locationData = useAppSelector((state) => state.locationData);
  const surveys = useAppSelector((state) => state.surveys);
  const zones = useAppSelector((state) => state.zones);
  const supportInventory = useAppSelector((state) => state.supportInventory);
  const services = useAppSelector((state) => state.services);
  const layers = useAppSelector((state) => state.layers);
  const filters = useAppSelector((state) => state.filters);
  const { zoom } = useAppSelector((state) => state.map);

  const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openTooltip, setOpenTooltip] = useState<number | null>(null);

  const { isLoading: isLoadingSurveys } = useSurveys();
  const { isLoading: isLoadingPaths } = usePaths();
  const { isLoading: isLoadingStations } = useStations();

  const Options: IOption[] = [
    {
      icon: "filter_alt",
      type: "Menu",
      content: <Filter />,
      toolTipText: "تعیین مبدا و مقصد",
    },
    {
      icon: "assignment",
      type: "Menu",
      active: supportInventory.visible,
      content: <Inventory />,
      toolTipText: "برنامه حرکت",
    },
    {
      icon: "date_range",
      type: "Menu",
      active: surveys.visible,
      content: <Time />,
      toolTipText: "ساعت و تاریخ",
    },
    {
      icon: "directions_car",
      type: "Menu",
      active: surveys.visible,
      content: <Cars />,
      toolTipText: "خودروها",
    },
    {
      icon: "airline_seat_recline_extra",
      type: "Menu",
      active: surveys.visible,
      content: <Drivers />,
      toolTipText: "رانندگان",
    },
    {
      icon: "layers",
      type: "Menu",
      toolTipText: "انتخاب لایه زون ها",
      content: <Zone />,
    },
    {
      icon: "delete",
      type: "Menu",
      toolTipText: "حذف فیلتر ها",
      content: <Delete setActiveOptionIndex={setActiveOptionIndex} />,
    },
    {
      icon: "dynamic_feedIcon",
      type: "Menu",
      content: <LayerControl />,
      toolTipText: "جابجایی لایه ها",
    },
    {
      icon: "room",
      type: "Toggle",
      toolTipText: "نمایش آدرس",
      active: locationData.visible,
      onClick: () => dispatch(toggleLocationData()),
    },
  ];

  const handleOpenTooltip = (index: number | null) => {
    setOpenTooltip(index);
  };

  const handleBtnColor = (index: number, isActivate?: boolean) => {
    if (index === 8 && zones.visible) {
      return "contained";
    }
    // if (index === 6 && filters.visibility.length > 0) {
    //   return "contained";
    // }
    if (isActivate || index === activeOptionIndex) {
      return "contained";
    }
    return "white";
  };

  // Logic functions
  const handleClick = (event: React.MouseEvent<HTMLElement>, index: number, item: IOption) => {
    const { currentTarget } = event;

    switch (item.type) {
      case "Menu":
        if (activeOptionIndex === index) {
          setActiveOptionIndex(null);
          return;
        }
        if (activeOptionIndex === null) {
          setAnchorEl(currentTarget);
          setActiveOptionIndex(index);
        } else {
          setActiveOptionIndex(null);
        }

        setTimeout(() => {
          setAnchorEl(currentTarget);
          setActiveOptionIndex(index);
        }, 250);
        break;
      case "Toggle":
        if (item.onClick) {
          item.onClick();
        }
        break;

      default:
        break;
    }
  };

  const handleMapZoom = (index: number) => {
    const zoomLevel = 0.5;
    const newZoom = index === 0 ? zoom + zoomLevel : zoom - zoomLevel;

    dispatch(setMapData({ zoom: newZoom }));
  };

  const handleShowIcon = (index: number, isActive: boolean, iconName: string) => {
    if (index === 1 && surveys.visible) {
      if ((surveys.surveyShowType === 0 || surveys.surveyShowType === 1) && isLoadingSurveys) {
        return <CircularProgress color="secondary" size={30} />;
      }
      if (surveys.surveyShowType === 2 && surveys.loading) {
        return <CircularProgress color="secondary" size={30} />;
      }
    }
    return <Icon baseClassName={!isActive ? "material-icons-outlined" : "material-icons"}>{iconName}</Icon>;
  };

  // UI functions
  const renderOptions = () =>
    Options.map((item, index) => {
      const isActive = index === activeOptionIndex;

      return (
        <div key={item.icon}>
          <Tooltip
            title={item.toolTipText}
            open={isActive ? false : openTooltip === index}
            onOpen={() => handleOpenTooltip(index)}
            onClose={() => handleOpenTooltip(null)}
            placement="top"
          >
            <Button
              sx={{ minWidth: "56px", p: 0, position: "relative", zIndex: "1" }}
              variant={handleBtnColor(index, item.active)}
              size="large"
              aria-describedby={`option-${index}`}
              onClick={(e) => handleClick(e, index, item)}
            >
              {handleShowIcon(index, isActive, item.icon)}
              <Box
                sx={{
                  position: "absolute",
                  bottom: "8px",
                  opacity: item.type === "Menu" && index === activeOptionIndex ? "1" : "0",
                  transition: "all 0.3s",
                }}
              >
                <Stack direction="row" spacing={0.3}>
                  {[1, 2, 3].map((i) => (
                    <Box key={`dot-${i}`} sx={{ width: "4px", height: "4px", bgcolor: "#fff", borderRadius: "50%" }} />
                  ))}
                </Stack>
              </Box>
            </Button>
          </Tooltip>

          <Popper
            id={`item-${index}`}
            sx={{ zIndex: "1" }}
            open={index === activeOptionIndex}
            anchorEl={anchorEl}
            placement="top"
            transition
            modifiers={[
              {
                name: "preventOverflow",
                enabled: true,
                options: { rootBoundary: "viewport", padding: LAYOUT_MARGIN + 56 + 8 },
              },
            ]}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Box sx={{ mb: 1 }}>{item.content} </Box>
              </Grow>
            )}
          </Popper>
        </div>
      );
    });

  return (
    <Stack direction="row" spacing={1}>
      {renderOptions()}
    </Stack>
  );
}
